import { useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { submitEnquiry } from "../lib/api";
import { MEASURES } from "../data/content";

const inputClass =
  "w-full rounded-xl border border-stone-300 bg-white px-4 py-3.5 font-figtree focus:ring-2 focus:ring-brand-accent focus:border-brand-accent outline-none transition-all";
const labelClass = "block text-sm font-medium text-stone-700 mb-1.5 font-figtree";

export default function QuoteForm() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", postcode: "",
    property_type: "", homeowner: "", message: "", measures: [],
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleMeasure = (name) =>
    setForm((f) => ({
      ...f,
      measures: f.measures.includes(name) ? f.measures.filter((m) => m !== name) : [...f.measures, name],
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitEnquiry(form);
      setDone(true);
      toast.success("Thank you! We'll be in touch within 24 hours.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="quote-success" className="bg-white border border-stone-200 rounded-2xl p-10 text-center">
        <CheckCircle2 className="w-14 h-14 text-brand-success mx-auto mb-4" />
        <h3 className="font-outfit text-2xl font-semibold text-brand mb-2">Enquiry received</h3>
        <p className="font-figtree text-stone-600">
          Thanks {form.name.split(" ")[0]}. One of our advisors will review your details and contact you within 24 hours to arrange your free, no-obligation survey.
        </p>
      </div>
    );
  }

  return (
    <form data-testid="quote-form" onSubmit={onSubmit} className="bg-white border border-stone-200 rounded-2xl p-6 sm:p-8 shadow-sm space-y-5">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className={labelClass}>Full name</label>
          <input data-testid="quote-name-input" required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputClass} placeholder="Jane Smith" />
        </div>
        <div>
          <label className={labelClass}>Email</label>
          <input data-testid="quote-email-input" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className={inputClass} placeholder="jane@email.com" />
        </div>
        <div>
          <label className={labelClass}>Phone</label>
          <input data-testid="quote-phone-input" required value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputClass} placeholder="07123 456789" />
        </div>
        <div>
          <label className={labelClass}>Postcode</label>
          <input data-testid="quote-postcode-input" required value={form.postcode} onChange={(e) => set("postcode", e.target.value)} className={inputClass} placeholder="SW1A 1AA" />
        </div>
      </div>

      <div>
        <label className={labelClass}>Which measures interest you?</label>
        <div className="flex flex-wrap gap-2">
          {MEASURES.map((m) => {
            const active = form.measures.includes(m.name);
            return (
              <button
                type="button"
                key={m.slug}
                data-testid={`quote-measure-${m.slug}`}
                onClick={() => toggleMeasure(m.name)}
                className={`rounded-full px-4 py-2 text-sm font-medium border transition-colors ${
                  active ? "bg-brand text-white border-brand" : "bg-white text-brand border-stone-300 hover:border-brand"
                }`}
              >
                {m.name}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className={labelClass}>Property type</label>
          <select data-testid="quote-property-select" value={form.property_type} onChange={(e) => set("property_type", e.target.value)} className={inputClass}>
            <option value="">Select…</option>
            <option>Detached</option>
            <option>Semi-detached</option>
            <option>Terraced</option>
            <option>Flat / Apartment</option>
            <option>Bungalow</option>
          </select>
        </div>
        <div>
          <label className={labelClass}>Are you the homeowner?</label>
          <select data-testid="quote-homeowner-select" value={form.homeowner} onChange={(e) => set("homeowner", e.target.value)} className={inputClass}>
            <option value="">Select…</option>
            <option>Yes</option>
            <option>No, I rent</option>
            <option>I'm a landlord</option>
          </select>
        </div>
      </div>

      <div>
        <label className={labelClass}>Anything else? (optional)</label>
        <textarea data-testid="quote-message-input" value={form.message} onChange={(e) => set("message", e.target.value)} rows={3} className={inputClass} placeholder="Tell us about your home or any questions…" />
      </div>

      <button
        data-testid="quote-submit-button"
        type="submit"
        disabled={loading}
        className="w-full bg-brand-accent text-white hover:bg-brand-accenthover transition-colors rounded-full px-8 py-4 font-medium inline-flex items-center justify-center gap-2 disabled:opacity-70"
      >
        {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Sending…</> : "Request my free survey"}
      </button>
      <p className="text-xs text-stone-500 text-center font-figtree">No upfront cost. No obligation. We respond within 24 hours.</p>
    </form>
  );
}
